import React from 'react';
import { Margin, SelectField } from '@ovotech/element-v4';
import { P } from '@ovotech/nebula';
import {
  SmartChargers,
  useChargeAnytimeContext,
  useChargerOptions,
  useChargeAnytimeDevices,
} from '@monovo/ev-anytime';

interface SmartChargerWithModelSelectionProps {
  manufacturerError?: string;
  modelError?: string;
}

export const SmartChargerWithModelSelection = ({
  manufacturerError,
  modelError,
}: SmartChargerWithModelSelectionProps) => {
  const {
    smartCharger,
    setSmartCharger,
    setSmartChargerModel,
    chargerDeviceId,
    setChargerDeviceId,
  } = useChargeAnytimeContext();
  const { manufacturers, models } = useChargerOptions();
  const { data: chargersData } = useChargeAnytimeDevices('chargers');
  const chargerDevices = chargersData?.devices ?? [];
  const chargerModels = models(smartCharger);
  const hasModels = chargerModels.length > 0;

  // Track if selected charger is Beta
  const selectedChargerDevice = chargerDevices.find(
    d => d.deviceId === chargerDeviceId,
  );
  const selectedChargerIsBeta =
    selectedChargerDevice?.status === 'BETA_COMPATIBLE';
  return (
    <>
      <SelectField
        id={'charger-manufacturer-selection'}
        label={'Make'}
        error={manufacturerError}
        value={smartCharger ?? ''}
        onValueChange={value => {
          setSmartCharger((value ?? null) as SmartChargers | null);
          setSmartChargerModel(null);
          setChargerDeviceId(null);
        }}
      >
        <option disabled value={''}>
          Select charger make
        </option>
        {manufacturers.map(({ value, label }) => {
          return (
            <option key={value} value={value}>
              {label}
            </option>
          );
        })}
      </SelectField>
      {hasModels && (
        <Margin top={4}>
          <SelectField
            id={'charger-models-selection'}
            label={'Model'}
            value={chargerDeviceId ?? ''}
            error={modelError}
            onValueChange={deviceId => {
              const device = chargerDevices.find(d => d.deviceId === deviceId);
              setSmartChargerModel(device?.model ?? (deviceId || null));
              setChargerDeviceId(deviceId ?? null);
            }}
          >
            <option disabled value={''}>
              Select charger model
            </option>
            {chargerModels.map(({ value, label }) => {
              const device = chargerDevices.find(d => d.deviceId === value);
              const isBeta = device?.status === 'BETA_COMPATIBLE';
              return (
                <option key={value} value={value}>
                  {label} {isBeta ? '(Beta)' : ''}
                </option>
              );
            })}
          </SelectField>
          {selectedChargerIsBeta && (
            <Margin top={2}>
              <P style={{ fontSize: '14px', color: 'GrayText' }}>
                This device is currently in Beta testing. You may experience
                difficulty connecting and charging while we continue to improve
                the integration with this model.
              </P>
            </Margin>
          )}
        </Margin>
      )}
    </>
  );
};
